<template>
  <v-layout
    wrap
  >
    <v-flex 
      xs12
      class="trader-investors"
    >
      <v-menu
        offset-y
        right
        bottom
      >
        <template v-slot:activator="{ on }">
          <v-btn
            :disabled="$apollo.loading"
            outlined 
            small
            text
            v-on="on"
          >
            {{ $t('rankings.investorType') }}
          </v-btn>
        </template>
        <v-sheet dense>
          <v-list-item
            @click="reload('filter', null)"
          >
            <v-list-item-title>{{ $t('traderdashboard.accountsAuM') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="reload('filter','UNLINKED')"
          >
            <v-list-item-title>{{ $t('rankings.unlinked') }}</v-list-item-title>
          </v-list-item>
        </v-sheet>
      </v-menu>
      <v-skeleton-loader
        v-if="$apollo.loading"
        type="table-tbody"
        :tile="true"
        class="mx-auto"
        width="100%"
        max-height="268"
      />
      <v-data-table  
        v-if="!$apollo.loading && !_.isEmpty(traderInvestors.data)" 
        class="table mt-5 text-no-wrap"
        :headers="headers"
        :footer-props="{
          'items-per-page-options': [10, 15, 25, 50, 100, 200]
        }"
        :locale="$i18n.locale"
        :items="traderInvestors.data"
        :items-per-page="traderInvestors.per_page"
        :page="traderInvestors.current_page"
        :server-items-length="traderInvestors.total"
        :hide-default-footer="_.isEmpty(traderInvestors.data)"
        dense
        @update:items-per-page="reload('count', $event)"
        @update:page="reload('page', $event)"
      >
        <template v-slot:item.name="{ item }">
          <router-link
            :to="`/connection/${item.id}?p=${item.project.id}`"
            class="mr-2"
          >
            <v-icon
              style="font-size:16px;"
            >
              mdi-eye
            </v-icon>
          </router-link>
          <small>{{ item.investor_name }} -  {{ item.connection_id }} </small>
        </template>
        <template v-slot:item.project_ticker="{ item }">
          <b class="text-primary">{{ item.project.ticker }}</b> 
          <v-tooltip
            v-if="((item.avg_deposit / item.init_deposit ) - 1) * 100 >= item.allowed_drawdown_ptc"
            top
          >
            <template v-slot:activator="{ on }">
              <v-icon
                class="text-positive"
                style="margin: -2px 3px 0px; font-size:14px;"
                v-on="on"
              >
                mdi-shield-check
              </v-icon>
            </template>
            {{ $t('rankings.investorSave') }}
          </v-tooltip>
        </template> 
        <template v-slot:item.pnl_this_month_ptc="{ item }">
          <b :class="colorPnL(item.pnl_this_month_ptc)">
            {{ item.pnl_this_month_ptc }}%
          </b>
          <v-tooltip
            top
          >
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <span
              :class="colorPnL(item.pnl_this_month)" 
            >{{ item.project.base_currency | renderSymbol }} {{ item.pnl_this_month | formatMoney }}</span>
          </v-tooltip> 
        </template> 
        <template v-slot:item.pnl_total_ptc="{ item }">
          <b :class="colorPnL(item.pnl_total_ptc)">
            {{ item.pnl_total_ptc }}%
          </b>
          <v-tooltip
            top
          >
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <span
              :class="colorPnL(item.pnl_total_ptc)" 
            ><b>{{ item.project.base_currency | renderSymbol }} {{ item.pnl_total | formatMoney }}</b></span>
          </v-tooltip> 
        </template> 

        <template v-slot:item.current_upnl_ptc="{ item }">
          <b :class="colorPnL(item.current_upnl_ptc)">{{ item.current_upnl_ptc }}%</b>
        </template> 
        <template v-slot:item.management_fee="{ item }">
          <b class="text-neutral mr-1">{{ item.project.base_currency | renderSymbol }} {{ item.management_fee | formatMoney }}</b>
          <v-tooltip
            v-if="item.fee_type == 'success'"
            top
          >
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-alpha-s-circle-outline
              </v-icon>
            </template>
            {{ $t('traderdashboard.successFeeHint') }}
          </v-tooltip> 
          <v-tooltip
            v-if="item.fee_type == 'management'"
            top
          >
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-alpha-m-circle-outline
              </v-icon>
            </template>
            {{ $t('traderdashboard.managementFeeHint', {'days': diffDays(item.connection_start)}) }}
          </v-tooltip> 
        </template> 
        <template v-slot:item.management_fee_paid="{ item }">
          <b class="text-neutral">{{ item.project.base_currency | renderSymbol }} {{ item.management_fee_paid | formatMoney }}</b>
        </template> 
        <template v-slot:item.current_balance="{ item }">
          <b>{{ item.project.base_currency | renderSymbol }} {{ item.current_balance | formatMoney }}</b>
        </template>
        <template v-slot:item.connection_start="{ item }">
          {{ item.connection_start | moment('L') }}
        </template>
      </v-data-table>
    </v-flex>  
    <v-flex
      v-if="!$apollo.loading && _.isEmpty(traderInvestors.data)" 
      class="not-found"
      py-10
    >
      <v-icon>mdi-ghost</v-icon>
      <p>{{ $t('traderdashboard.accountsNotFound') }}</p> 
    </v-flex>
  </v-layout>
</template>

<script>

const TRADER_INVESTORS_QUERY = require('@/graphql/queries/TraderInvestors.gql');


export default {
  name: 'TraderInvestors',
  data() {
    return {
      expanded: [],
      loading: false,
      params: {
        page: 1,
        count: 25
      },
      headers: [
        {
          sortable: false,
          text: this.$t('dashboard.name'),
          value: 'name'
        },
        {
          sortable: false,
          text: '',
          value: 'project_ticker'
        },
        {
          sortable: false,
          text: this.$t('globals.pnl_this_month'),
          value: 'pnl_this_month_ptc'
        },
        {
          sortable: false,
          text: this.$t('globals.pnl_total'),
          value: 'pnl_total_ptc'
        },
        {
          sortable: false,
          text: this.$t('rankings.upnl'),
          value: 'current_upnl_ptc'
        },
        {
          sortable: false,
          text: this.$t('traderdashboard.earnedTotal'),
          value: 'management_fee'
        },
        {
          sortable: false,
          text: this.$t('traderdashboard.paidTotal'),
          value: 'management_fee_paid'
        },
        {
          sortable: false,
          text: this.$t('rankings.currentBalance'),
          value: 'current_balance'
        },
        {
          sortable: false,
          text: this.$t('rankings.dateStart'),
          value: 'connection_start'
        },
      ]
    }
  },
  methods: {
    diffDays(from) {
      var a = this.$moment();
      var b = this.$moment(from);
      return a.diff(b, 'days') 
    },
    reload(key, value) {
      if (value == null) {
        delete this.params[key]
      } else {
        this.params[key] = value
      }

      this.$apollo.queries.traderInvestors.fetchMore({
        variables: this.params,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          return fetchMoreResult
        },
      })
    },
  },
  apollo: {
    traderInvestors: {
      query: TRADER_INVESTORS_QUERY,
      fetchPolicy: 'network-only',
      variables() {
        return this.params
      },
      result ({ data, loading, networkStatus }) {
        if (loading == false && !_.isEmpty(data.traderInvestors.data)) {
          data.traderInvestors.data.map((item)=>{
            item.commission_total = item.pnl_total > 0 ? (parseFloat(item.pnl_total) * parseFloat(item.management_fee) / 100) : 0
            return item
         })
        }
      },
    }
  }
}
</script>

<style lang="scss">
.trader-investors {
  .table {
    .v-icon {
      margin-top:-2.5px;
      font-size:13px;
    }
  }    
}

</style>
